import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Player } from "../types";
import { WolfSelection } from "../activeRound";

// Wolf per-hole picker — same magnified scroll-wheel UX as the score entry
// and the Oosler picker, so the gesture is identical for the user. Fires when
// the first score on a hole commits. Items, in order:
//   Lone Wolf   ←  default (solo, declared after the tee shots)
//   Blind Wolf  ←  solo declared before any shot → multiplied settlement
//   <player>    ←  the three non-Wolf players, as a partner
// Picking a player teams the Wolf with them for the hole; picking Lone/Blind
// Wolf plays the Wolf solo against the other three.

interface Props {
  holeNumber: number;
  // The Wolf for this hole (rotation) — excluded from the partner choices and
  // named in the picker header.
  wolfId: string;
  wolfName: string;
  players: Player[];
  // When false, the "Blind Wolf" option is hidden from the wheel (the Blind
  // Wolf Multiplier setting is off for this round).
  blindEnabled: boolean;
  anchorRect: DOMRect;
  onSelect: (selection: WolfSelection) => void;
  onClose: () => void;
}

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 3;
const WHEEL_WIDTH = 180;
const PADDING_X = 12;
// Fits the big "Wolf" title plus the small whose-wolf name line below it.
const LABEL_HEIGHT = 62;

const LONE_VALUE = "__lone__";
const BLIND_VALUE = "__blind__";

interface WheelItem {
  value: string;
  label: string;
}

function toSelection(value: string): WolfSelection {
  if (value === LONE_VALUE) return { partnerId: null, blind: false };
  if (value === BLIND_VALUE) return { partnerId: null, blind: true };
  return { partnerId: value, blind: false };
}

export default function WolfSelectionModal({
  holeNumber,
  wolfId,
  wolfName,
  players,
  blindEnabled,
  anchorRect,
  onSelect,
  onClose,
}: Props) {
  const items: WheelItem[] = [
    { value: LONE_VALUE, label: "Lone Wolf" },
    ...(blindEnabled ? [{ value: BLIND_VALUE, label: "Blind Wolf" }] : []),
    ...players
      .filter((p) => p.id !== wolfId)
      .map((p, i) => ({
        value: p.id,
        label: p.name || `Player ${i + 1}`,
      })),
  ];

  const [value, setValue] = useState<string>(items[0].value);
  const valueRef = useRef(value);
  valueRef.current = value;

  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const armedRef = useRef(false);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el) {
      const idx = items.findIndex((it) => it.value === valueRef.current);
      if (idx >= 0) el.scrollTop = idx * ITEM_HEIGHT;
    }
    const t = setTimeout(() => {
      armedRef.current = true;
    }, 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handle = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
        const clamped = Math.max(0, Math.min(items.length - 1, idx));
        const next = items[clamped]?.value;
        if (next !== undefined && next !== valueRef.current) setValue(next);
      }, 80);
    };
    el.addEventListener("scroll", handle, { passive: true });
    return () => {
      el.removeEventListener("scroll", handle);
      if (timer) clearTimeout(timer);
    };
  }, [items]);

  const commitAndClose = useCallback(() => {
    onSelect(toSelection(valueRef.current));
    onClose();
  }, [onSelect, onClose]);

  const onItemTap = useCallback(
    (next: string) => {
      valueRef.current = next;
      setValue(next);
      onSelect(toSelection(next));
      onClose();
    },
    [onSelect, onClose],
  );

  useEffect(() => {
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!armedRef.current) return;
      const target = e.target as Node;
      if (overlayRef.current && overlayRef.current.contains(target)) return;
      commitAndClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "Enter") commitAndClose();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [commitAndClose, onClose]);

  const wheelH = VISIBLE_COUNT * ITEM_HEIGHT;
  const pickerW = WHEEL_WIDTH + PADDING_X * 2;
  const pickerH = wheelH + LABEL_HEIGHT + PADDING_X * 2;
  const cellCenterX = anchorRect.left + anchorRect.width / 2;
  const cellCenterY = anchorRect.top + anchorRect.height / 2;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = cellCenterX - pickerW / 2;
  let top = cellCenterY - pickerH / 2;
  if (left < 4) left = 4;
  if (left + pickerW > vw - 4) left = vw - 4 - pickerW;
  if (top < 4) top = 4;
  if (top + pickerH > vh - 4) top = vh - 4 - pickerH;

  return (
    <div className="cg-score-pick-backdrop" aria-hidden>
      <div
        ref={overlayRef}
        className="cg-score-pick"
        role="dialog"
        aria-label={`Wolf selection for hole ${holeNumber}`}
        style={{ left, top, width: pickerW, height: pickerH }}
      >
        <div className="cg-score-pick-cols">
          <div className="cg-score-pick-col" style={{ width: WHEEL_WIDTH }}>
            <div className="cg-pick-step cg-pick-step-wolf">Wolf</div>
            <div className="cg-score-pick-label">
              {wolfName || `Player ${((holeNumber - 1) % 4) + 1}`}
            </div>
            <div
              className="cg-score-pick-wheel"
              style={{ height: wheelH, width: WHEEL_WIDTH }}
            >
              <div
                className="cg-score-pick-indicator"
                style={{ top: ITEM_HEIGHT, height: ITEM_HEIGHT }}
              />
              <div
                ref={scrollerRef}
                className="cg-score-pick-scroller"
                style={{ height: wheelH }}
              >
                <div style={{ height: ITEM_HEIGHT }} aria-hidden />
                {items.map((it) => (
                  <div
                    key={it.value}
                    className={`cg-score-pick-item${
                      it.value === value ? " cg-score-pick-item-on" : ""
                    }`}
                    style={{ height: ITEM_HEIGHT }}
                    onClick={() => onItemTap(it.value)}
                  >
                    {it.label}
                  </div>
                ))}
                <div style={{ height: ITEM_HEIGHT }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
